// Builds the sample Word document for a profile (styles defined by name only).
// It is the preview source and the "Sample .docx" download.
import {
  AlignmentType, Document, LevelFormat, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType,
} from "docx";
import JSZip from "jszip";
import { ruleName } from "./labels.js";

const LOREM = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
  "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore.",
  "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt.",
];
const lorem = (i) => LOREM[i % LOREM.length];

const BLOCK_SAMPLES = ["title", "heading", "quote", "callout", "code", "caption"];
const RUN_PROPERTY_OPTIONS = {
  b: { bold: true }, i: { italics: true }, u: { underline: {} }, strike: { strike: true },
  "small-caps": { smallCaps: true }, "all-caps": { allCaps: true }, highlight: { highlight: "yellow" },
};

export function styleId(name) {
  return name.replace(/[^A-Za-z0-9]/g, "") || "Style";
}

export function specimenRules(profile) {
  return profile.rules.filter((r) => r.raw === undefined && r.group !== "quiet");
}

function attempt(rule, make) {
  try {
    return make();
  } catch (error) {
    throw new Error(`Couldn't add “${ruleName(rule)}” to the sample document: ${error.message}`);
  }
}

function defineStyles(rules) {
  const paragraphStyles = [];
  const characterStyles = [];
  const ids = new Map();
  const used = new Set();
  for (const rule of rules) {
    if (!rule.style || rule.prefix || (rule.match !== "p" && rule.match !== "r")) continue;
    const key = `${rule.match}|${rule.style.toLowerCase()}`;
    if (ids.has(key)) continue;
    const base = styleId(rule.style);
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}${n}`;
    used.add(id);
    ids.set(key, id);
    if (rule.match === "p") {
      const heading = /^heading ([1-6])$/i.exec(rule.style);
      paragraphStyles.push({
        id, name: rule.style, basedOn: "Normal", next: "Normal", quickFormat: true,
        ...(heading ? { paragraph: { outlineLevel: Number(heading[1]) - 1 } } : {}),
      });
    } else {
      characterStyles.push({ id, name: rule.style, quickFormat: true });
    }
  }
  return { paragraphStyles, characterStyles, idFor: (rule) => ids.get(`${rule.match}|${rule.style.toLowerCase()}`) };
}

const tableStyleName = (rule) => (rule.style ? (rule.prefix ? `${rule.style} Sample` : rule.style) : null);

function sampleTable(styleName) {
  const cell = (text) => new TableCell({ children: [new Paragraph(text)] });
  return new Table({
    ...(styleName ? { style: styleId(styleName) } : {}),
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ tableHeader: true, children: ["Item", "Quantity", "Notes"].map(cell) }),
      new TableRow({ children: ["Lorem", "3", "Ipsum dolor sit amet"].map(cell) }),
      new TableRow({ children: ["Consectetur", "12", "Adipiscing elit"].map(cell) }),
    ],
  });
}

function buildBody(profile, rules, idFor, focus) {
  const children = [];
  const tableStyles = [];
  const plain = (text) => new Paragraph({ children: [new TextRun(text)] });
  const styled = (rule, text) => attempt(rule, () => new Paragraph({ style: idFor(rule), children: [new TextRun(text)] }));
  const section = (title) => {
    if (focus) return;
    children.push(new Paragraph({ spacing: { before: 360, after: 120 }, children: [new TextRun({ text: title.toUpperCase(), bold: true, color: "5B7378", size: 18 })] }));
  };
  const blockRules = (sample) => rules.filter((r) => r.match === "p" && r.style && !r.prefix && r.sample === sample);
  const otherParagraphRules = rules.filter((r) => r.match === "p" && r.style && !r.prefix && !BLOCK_SAMPLES.includes(r.sample));

  blockRules("title").forEach((rule, i) =>
    children.push(styled(rule, !focus && /^title$/i.test(rule.style) ? `${profile.name} sample` : `${rule.style}: ${lorem(i)}`)));
  if (!focus) {
    children.push(
      plain(`This document uses every Word style the “${profile.name}” profile maps to HTML. Use it as a template, or copy its styles into your own documents.`),
      plain(`Normal: ${lorem(0)} ${lorem(1)}`),
    );
  }

  const headings = blockRules("heading");
  if (headings.length) {
    section("Headings");
    headings.forEach((rule, i) => children.push(styled(rule, `${rule.style}: ${lorem(i)}`)));
  }

  if (otherParagraphRules.length) {
    section("Paragraphs");
    otherParagraphRules.forEach((rule, i) => children.push(styled(rule, `${rule.style}: ${lorem(i + 1)}`)));
  }

  const inline = rules.filter((r) => r.sample === "inline" && (RUN_PROPERTY_OPTIONS[r.match] || (r.match === "r" && r.style && !r.prefix)));
  if (inline.length) {
    section("Inline styles");
    const runs = [new TextRun(focus ? "Example: " : "Inline styles: ")];
    inline.forEach((rule, i) => {
      const options = rule.match === "r" ? { style: idFor(rule) } : RUN_PROPERTY_OPTIONS[rule.match];
      runs.push(attempt(rule, () => new TextRun({ text: ruleName(rule), ...options })));
      runs.push(new TextRun(i === inline.length - 1 ? "." : ", "));
    });
    children.push(new Paragraph({ children: runs }));
  }

  if (!focus || rules.some((r) => r.sample === "list")) {
    section("Lists");
    const bullet = (text, level) => new Paragraph({ text, numbering: { reference: "specimen-bullets", level } });
    const number = (text, level) => new Paragraph({ text, numbering: { reference: "specimen-numbers", level } });
    children.push(
      bullet("Bulleted item", 0), bullet("Nested bulleted item", 1), bullet("Third-level item", 2), bullet("Another bulleted item", 0),
      plain("Numbered lists:"),
      number("First step", 0), number("Sub-step", 1), number("Second step", 0),
    );
  }

  const quotes = blockRules("quote");
  if (quotes.length) {
    section("Quotes");
    quotes.forEach((rule, i) => children.push(styled(rule, `${rule.style}: ${lorem(i + 2)}`)));
  }

  const callouts = blockRules("callout");
  if (callouts.length) {
    section("Callouts");
    callouts.forEach((rule, i) => children.push(styled(rule, `${rule.style}: ${lorem(i)}`)));
    if (!focus) children.push(plain("Consecutive paragraphs in the same callout style join into one box. Put a normal paragraph between two boxes of the same type to keep them separate."));
  }

  const code = blockRules("code");
  if (code.length) {
    section("Code");
    for (const rule of code) {
      if (!focus) children.push(plain(`${rule.style}:`)); // also stops neighbouring code styles merging
      ["function hello() {", `  return "${rule.style}";`, "}"].forEach((line) => children.push(styled(rule, line)));
    }
  }

  const tables = rules.filter((r) => r.match === "table");
  if (tables.length) {
    section("Tables");
    for (const rule of tables) {
      const name = tableStyleName(rule);
      if (name) tableStyles.push(name);
      if (!focus) children.push(plain(name ? `Table style “${name}”:` : "Table with no table style:"));
      children.push(attempt(rule, () => sampleTable(name)));
    }
  }

  blockRules("caption").forEach((rule, i) => children.push(styled(rule, `${rule.style}: Figure ${i + 1}. ${lorem(i)}`)));

  return { children, tableStyles };
}

function listConfig(reference, format) {
  return {
    reference,
    levels: [0, 1, 2].map((level) => ({
      level,
      format,
      text: format === LevelFormat.BULLET ? ["•", "◦", "▪"][level] : `%${level + 1}.`,
      alignment: AlignmentType.LEFT,
      style: { paragraph: { indent: { left: 720 * (level + 1), hanging: 360 } } },
    })),
  };
}

const escapeXml = (text) => text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[c]);

// docx can't declare named table styles, so add minimal ones for mammoth (and Word) to see.
async function injectTableStyles(buffer, names) {
  if (!names.length) return buffer;
  const zip = await JSZip.loadAsync(buffer);
  const xml = await zip.file("word/styles.xml").async("string");
  const border = (side) => `<w:${side} w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>`;
  const styles = names.map((name) =>
    `<w:style w:type="table" w:customStyle="1" w:styleId="${styleId(name)}"><w:name w:val="${escapeXml(name)}"/>` +
    `<w:tblPr><w:tblBorders>${["top", "left", "bottom", "right", "insideH", "insideV"].map(border).join("")}</w:tblBorders></w:tblPr></w:style>`);
  zip.file("word/styles.xml", xml.replace("</w:styles>", `${styles.join("")}</w:styles>`));
  return zip.generateAsync({ type: "arraybuffer" });
}

// only: build a focused sample containing just this rule (used by the rule editor).
export async function buildSpecimen(profile, { only } = {}) {
  const rules = only ? [only] : specimenRules(profile);
  const { paragraphStyles, characterStyles, idFor } = defineStyles(rules);
  const { children, tableStyles } = buildBody(profile, rules, idFor, Boolean(only));
  const doc = new Document({
    creator: "Mammoth Press",
    title: `${profile.name} sample`,
    styles: { paragraphStyles, characterStyles },
    numbering: { config: [listConfig("specimen-bullets", LevelFormat.BULLET), listConfig("specimen-numbers", LevelFormat.DECIMAL)] },
    sections: [{ children }],
  });
  return injectTableStyles(await Packer.toArrayBuffer(doc), [...new Set(tableStyles)]);
}
